import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
	Box,
	FormControlLabel,
	FormGroup,
	IconButton,
	InputAdornment,
	Switch,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { TextFieldElement } from 'react-hook-form-mui';
import * as yup from 'yup';
import {
	Permission,
	permissionDisplayName,
	PermissionsKeyedShape,
} from '../../../../../../permissions';
import { FORM_MESSAGE } from '../../../../../fastForm/data/constants';
import {
	ROLE_FORM_DISPLAY_NAME_FIELD_LABEL,
	ROLE_FORM_FILTER_CLEAR_VISIBLE_BUTTON,
	ROLE_FORM_FILTER_INPUT_LABEL,
	ROLE_FORM_FILTER_SAVE_BUTTON,
	ROLE_FORM_FILTER_SELECT_VISIBLE_BUTTON,
	ROLE_FORM_NAME_FIELD_LABEL,
	ROLE_FORM_UI_PERMISSION_INFO_LABEL,
	ROLE_FORM_UI_PERMISSION_TOGGLE_LABEL,
} from '../../../data/constants';

const headerHeight = 88;
const headerHeightWithErrorMessage = 109;
const footerHeight = 64;

export interface RoleCreateForm extends PermissionsKeyedShape {
	name: string;
	displayName: string;
	permissions: Permission[];
}

interface RoleFormProps {
	onSubmit: SubmitHandler<RoleCreateForm>;
	formTitle: string;
	permissions: PermissionsKeyedShape;
	permissionDefaults: PermissionsKeyedShape;
	roleName?: string;
	displayName?: string;
	edit?: boolean;
}

const RoleForm = ({
	onSubmit,
	formTitle,
	permissions,
	permissionDefaults,
	roleName,
	displayName,
	edit,
}: RoleFormProps) => {
	const [filterText, setFilterText] = React.useState<string>('');
	const [visiblePermissions, setVisiblePermissions] =
		React.useState<PermissionsKeyedShape>(permissions);

	const {
		control,
		setValue,
		watch,
		handleSubmit,
		formState: { errors },
	} = useForm<RoleCreateForm>({
		defaultValues: {
			name: roleName || '',
			displayName: displayName || '',
			permissions: [],
			...permissionDefaults,
		},
		resolver: yupResolver(
			yup.object().shape({
				name: yup
					.string()
					.required(FORM_MESSAGE.required)
					.test(
						'no-special-characters',
						FORM_MESSAGE.noSpecialCharactersOrWhitespace,
						(value) => {
							if (value) {
								const regex = /^[a-zA-Z0-9_\-~]+$/;
								return regex.test(value);
							}
							return true;
						},
					),
				displayName: yup
					.string()
					.required(FORM_MESSAGE.required)
					.test(
						'no-special-characters',
						FORM_MESSAGE.noSpecialCharacters,
						(value) => {
							if (value) {
								const regex = /^[0-9a-zA-Z!\-_.*()~\s]+$/;
								return regex.test(value);
							}
							return true;
						},
					),
			}),
		),
	});

	const filterVisiblePermissions = (filter: string) => {
		setFilterText(filter);
		const filteredPermissions = Object.keys(permissions).reduce(
			(acc, key: keyof PermissionsKeyedShape) => {
				acc[key] = permissions[key].filter((permission) => {
					// Because we change "*"" to "all" in the UI, we need to translate it to filter "all" as "*""
					const allRegex = /\ba(l(l)?)?\b/;
					const globalRegex = /\bg(l(o(b(a(l)?)?)?)?)?\b/;
					const translatedFilter =
						filter.match(allRegex) || filter.match(globalRegex) ? '*' : filter;
					return permission
						.toLowerCase()
						.includes(translatedFilter.toLowerCase());
				});
				return acc;
			},
			{} as PermissionsKeyedShape,
		);
		setVisiblePermissions(filteredPermissions);
	};

	const selectAllVisiblePermissions = () => {
		Object.keys(visiblePermissions).forEach(
			(key: keyof PermissionsKeyedShape) => {
				const selectedPermissions = watch(key); // ['quote.*', 'quote.create', 'quote.delete']
				const visiblePermissionsArray = visiblePermissions[key]; // ['quote.*', 'quote.create', 'quote.update', 'quote.fetch', 'quote.delete']
				const permissionsToAdd = visiblePermissionsArray.filter(
					(permission) => !selectedPermissions.includes(permission),
				);
				setValue(key, selectedPermissions.concat(permissionsToAdd));
			},
		);
	};

	const clearAllVisiblePermissions = () => {
		Object.keys(visiblePermissions).forEach(
			(key: keyof PermissionsKeyedShape) => {
				const selectedPermissions = watch(key); // ['quote.*', 'quote.create', 'quote.delete']
				const visiblePermissionsArray = visiblePermissions[key]; // ['quote.*', 'quote.create', 'quote.update', 'quote.fetch', 'quote.delete']
				const permissionsToClear = selectedPermissions.filter(
					(permission) => !visiblePermissionsArray.includes(permission),
				);
				setValue(key, permissionsToClear);
			},
		);
	};

	const uiPermissionsToggled =
		watch('product').includes('product.fetch') &&
		watch('ontology').includes('ontology.fetch') &&
		watch('workflow').includes('workflow.fetch') &&
		watch('tenant').includes('tenant.fetch');

	const toggleUIPermissions = () => {
		Object.keys(permissions).forEach((key: keyof PermissionsKeyedShape) => {
			if (
				key === 'product' ||
				key === 'ontology' ||
				key === 'workflow' ||
				key === 'tenant'
			) {
				const selectedPermissions = watch(key); // ['quote.*', 'quote.create', 'quote.delete']
				const visiblePermissionsArray = visiblePermissions[key]; // ['quote.*', 'quote.create', 'quote.update', 'quote.fetch', 'quote.delete']
				if (uiPermissionsToggled) {
					setValue(key, []);
				} else {
					const permissionsToAdd = visiblePermissionsArray.filter(
						(permission) =>
							!selectedPermissions.includes(permission) &&
							!permission.includes('*'),
					);
					setValue(key, selectedPermissions.concat(permissionsToAdd));
				}
			}
		});
	};

	const clearFilter = () => {
		filterVisiblePermissions('');
	};

	return (
		<Paper
			sx={{
				flex: 1,
				height: 'calc(100% - 24px)',
			}}
			component='form'
			onSubmit={handleSubmit(onSubmit)}
		>
			<Stack spacing={2} sx={{ pl: 4, pr: 4, pt: 2, pb: 2 }} direction='row'>
				<TextFieldElement
					disabled={edit}
					variant='outlined'
					name='name'
					control={control}
					fullWidth
					label={ROLE_FORM_NAME_FIELD_LABEL()}
				/>
				<TextFieldElement
					disabled={edit}
					variant='outlined'
					name='displayName'
					control={control}
					fullWidth
					label={ROLE_FORM_DISPLAY_NAME_FIELD_LABEL()}
				/>
			</Stack>
			<Box
				sx={{
					height: `calc(100% - ${
						Object.keys(errors).length > 0
							? headerHeightWithErrorMessage
							: headerHeight
					}px - ${footerHeight}px)`,
					overflowY: 'auto',
					pl: 4,
					pr: 4,
					pt: 2,
					pb: 2,
					mt: 0,
					bgcolor: 'background.default',
					p: 4,
					boxShadow:
						'inset 0px 11px 8px -10px #CCC, inset 0px -11px 8px -10px #CCC',
				}}
			>
				<Typography variant='h6'>{formTitle}</Typography>
				<Stack direction='row' justifyContent='space-between' sx={{ mt: 2 }}>
					<Stack direction='row' justifyContent='flex-start' spacing={2}>
						<TextField
							label={ROLE_FORM_FILTER_INPUT_LABEL()}
							size='small'
							onChange={(e) => filterVisiblePermissions(e.target.value)}
							variant='outlined'
							value={filterText}
							InputProps={{
								endAdornment: (
									<InputAdornment position='start'>
										<IconButton
											aria-label='clear filter'
											onClick={clearFilter}
											edge='end'
										>
											{filterText !== '' ? <ClearIcon /> : <SearchIcon />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
						<Button
							variant='contained'
							color='secondary'
							onClick={selectAllVisiblePermissions}
							startIcon={<CheckCircleOutlineIcon />}
						>
							{ROLE_FORM_FILTER_SELECT_VISIBLE_BUTTON()}
						</Button>
						<Button
							variant='text'
							onClick={clearAllVisiblePermissions}
							startIcon={<HighlightOffIcon />}
						>
							{ROLE_FORM_FILTER_CLEAR_VISIBLE_BUTTON()}
						</Button>
					</Stack>
					<Stack
						direction='row'
						justifyContent='flex-start'
						spacing={2}
						alignItems='center'
					>
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										checked={uiPermissionsToggled}
										color='secondary'
										onChange={toggleUIPermissions}
									/>
								}
								label={
									<Stack direction='row' alignItems='center' spacing={2}>
										<Typography>
											{ROLE_FORM_UI_PERMISSION_TOGGLE_LABEL()}
										</Typography>
									</Stack>
								}
							/>
						</FormGroup>
						<Tooltip title={ROLE_FORM_UI_PERMISSION_INFO_LABEL()}>
							<InfoOutlinedIcon
								sx={{ color: 'text.secondary', cursor: 'pointer' }}
							/>
						</Tooltip>
					</Stack>
				</Stack>
				{Object.keys(visiblePermissions).map(
					(key: keyof PermissionsKeyedShape) =>
						visiblePermissions[key].length > 0 && (
							<Box key={key}>
								<Typography sx={{ mt: 2, mb: 1 }}>
									{permissionDisplayName(visiblePermissions[key][0], {
										excludeAction: true,
									})}
								</Typography>
								<Stack spacing={1} direction='row'>
									{visiblePermissions[key].map((permission) => (
										<Controller
											key={permission}
											name={key}
											control={control}
											render={({ field }) => {
												return (
													<Chip
														size='small'
														onClick={() => {
															if (field.value.includes(permission)) {
																field.onChange(
																	field.value.filter(
																		(v: string) => v !== permission,
																	),
																);
															} else {
																field.onChange([...field.value, permission]);
															}
														}}
														label={permissionDisplayName(permission, {
															excludeScope: true,
														})}
														color={
															field.value.includes(permission)
																? 'secondary'
																: 'default'
														}
														variant='filled'
													/>
												);
											}}
										/>
									))}
								</Stack>
							</Box>
						),
				)}
			</Box>
			<Stack
				sx={{ height: footerHeight, pl: 2, pr: 2 }}
				direction='row'
				alignItems='center'
				justifyContent='flex-end'
			>
				<Button variant='contained' color='secondary' type='submit'>
					{ROLE_FORM_FILTER_SAVE_BUTTON()}
				</Button>
			</Stack>
		</Paper>
	);
};

export default RoleForm;
